import React, { useCallback, useState } from 'react';

export type UseComboboxListNavigationOptions = {
  itemCount: number;
  // when false the arrows belong to the field, not the list
  isOpen: boolean;
};

export type ComboboxListNavigation = {
  activeIndex: number;
  // for pointer hover, which highlights without moving through the list
  setActiveIndex: (index: number) => void;
  reset: () => void;
  // true when the key moved the highlight, so the caller stops handling it
  onArrowKeyDown: (event: React.KeyboardEvent) => boolean;
};

export const useComboboxListNavigation = ({
  itemCount,
  isOpen,
}: UseComboboxListNavigationOptions): ComboboxListNavigation => {
  const [index, setIndex] = useState(0);

  // Clamped on read, so a list re-filtering under the highlight keeps it where it still fits.
  const activeIndex = Math.min(index, Math.max(itemCount - 1, 0));

  const reset = useCallback(() => setIndex(0), []);

  const onArrowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen || !itemCount) {
        return false;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return false;
      }

      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setIndex((current) => {
        const from = Math.min(current, Math.max(itemCount - 1, 0));
        return (from + delta + itemCount) % itemCount;
      });
      return true;
    },
    [isOpen, itemCount],
  );

  return { activeIndex, setActiveIndex: setIndex, reset, onArrowKeyDown };
};
